import { forEach, keyBy, omit, set } from 'lodash';
import Thread, { IVR_TAGS } from 'src/models/Thread/Thread';

import { FIELD_MAPPER } from 'src/presentation/components/pages/threads/ThreadsConstants';
import { ThreadWithNodeMap } from '../hooks/useOnSave';

const newThreadTemplate = () => {
  return {
    id: 'WISMO',
    version: 1,
    description: 'Retail Order Status',
    tags: [IVR_TAGS.CONSUMER_SELF_SERVICE, IVR_TAGS.WISMO],
    rootNodeId: 'email_auth_code',
    debug: false,
    nodes: [
      {
        type: 'IVR_NODE_DATA_DISPLAY',
        ivrNode: {
          id: 'display_order',
          name: 'display order',
          description: 'display customer order information',
          displayType: 'ORDER_STATUS',
          data: '{{ $length := len .order_data }}{{ if gt $length 3 }}{{toJson slice .order_data 0 3}}{{else}}{{toJson .order_data}}{{end}}',
          config: {
            missingFulfillmentMessage: 'We’re preparing your order for shipment.',
            missingStatusAndEtaMessage: 'We’re awaiting carrier pickup.',
            missingEtaMessage: 'We’ll post the estimated arrival date soon.',
          },
          nextId: 'verify_success',
        },
      },
      {
        type: 'IVR_NODE_MESSAGE',
        ivrNode: {
          id: 'wait_route_to_hero',
          name: 'wait route to hero',
          description: 'route to hero wait message',
          message: {
            message: 'Got it! Let’s connect you with someone who can help.',
            type: 'TEXT',
          },
          nextId: 'route_to_hero',
        },
      },
      {
        type: 'IVR_NODE_VERIFY_AUTH_CODE',
        ivrNode: {
          id: 'verify_auth_code',
          name: 'verify auth code',
          description: "Confirming a customer's authentication code",
          authCode: '{{.email_auth_code.code}}',
          customerId: '{{.customer_id}}',
          successNodeId: 'auth_code_success',
          failureNodeId: 'auth_code_failure',
          errorNodeId: 'error',
          timeout: 5000000000,
        },
      },
      {
        type: 'IVR_NODE_EXTERNAL_DATA',
        ivrNode: {
          id: 'order_data',
          name: 'order data',
          description: 'retrieve order transaction data',
          gladlyEntityType: 'CUSTOMER',
          gladlyEntityId: '{{.customer_id}}',
          dataType: {
            name: 'gladly_order',
            majorVersion: 1,
            minorVersion: 0,
          },
          numItems: 3,
          dataTransformation: '',
          nextId: 'check_order_data',
          errorNodeId: 'error',
        },
      },
      {
        type: 'IVR_NODE_VARIABLE_MATCH',
        ivrNode: {
          id: 'check_order_data',
          name: 'check order data',
          description: 'check presence of order data',
          variableMatches: [
            {
              expression: '{{ if empty .order_data }}0{{end}}',
              pattern: '0',
              nextId: 'no_orders_found',
            },
          ],
          defaultNextId: 'orders_found',
          errorNodeId: 'error',
        },
      },
      {
        type: 'IVR_NODE_MESSAGE',
        ivrNode: {
          id: 'orders_found',
          name: 'orders found',
          description: 'Orders were found for the customer',
          message: {
            message: 'Here’s the status of your recent order:',
            type: 'TEXT',
          },
          nextId: 'display_order',
        },
      },
      {
        type: 'IVR_NODE_MESSAGE',
        ivrNode: {
          id: 'no_orders_found',
          name: 'no orders found',
          description: 'No orders were found for the customer',
          message: {
            message:
              'We were unable to find an order associated with that email address. But don’t worry, one of our support heroes will be with you shortly to help.',
            type: 'TEXT',
          },
          nextId: 'route_to_hero',
        },
      },
      {
        type: 'IVR_NODE_MENU',
        ivrNode: {
          id: 'verify_success',
          name: 'verify success',
          description: 'check with the customer whether or not they were able to self serve',
          message: {
            message: 'Did this answer your question?',
            type: 'TEXT',
          },
          menu: [
            {
              dtmf: '1',
              nextId: 'self_served',
              label: 'Yes, it did',
            },
            {
              dtmf: '2',
              nextId: 'wait_route_to_hero',
              label: 'No, speak to support hero',
            },
          ],
          badSelectionNodeId: 'error',
        },
      },
      {
        type: 'IVR_NODE_SELF_SERVED',
        ivrNode: {
          id: 'self_served',
          name: 'self served',
          description: 'customer has successfully self served',
          category: 'WISMO',
          customerId: '{{.customer_id}}',
          nextId: 'next_steps',
        },
      },
      {
        type: 'IVR_NODE_MENU',
        ivrNode: {
          id: 'next_steps',
          name: 'next steps',
          description: 'ask the customer what they would like to do next',
          message: {
            message: 'Is there anything else you need help with?',
            type: 'TEXT',
          },
          menu: [
            {
              dtmf: '1',
              nextId: 'goodbye',
              label: "Thanks, I'm good",
            },
            {
              dtmf: '2',
              nextId: 'wait_route_to_hero',
              label: 'Speak to support hero',
            },
          ],
          badSelectionNodeId: 'error',
        },
      },
      {
        type: 'IVR_NODE_ROUTE',
        ivrNode: {
          id: 'route_to_hero',
          name: 'route to hero',
          description: 'route customer to support hero',
        },
      },
      {
        type: 'IVR_NODE_VARIABLE',
        ivrNode: {
          id: 'customer_id',
          name: 'customer ID',
          description: 'consistent customer ID to be used throughout the IVR configuration',
          expression: '{{.email_auth_code.customerId}}',
          nextId: 'authentication_code_prompt',
          errorNodeId: 'error',
        },
      },
      {
        type: 'IVR_NODE_MESSAGE',
        ivrNode: {
          id: 'authentication_code_prompt',
          name: 'authentication code prompt',
          description: 'Prompt customer to confirm authentication code',
          message: {
            message:
              'Got it! To confirm it’s you, please type in the code that was sent to the email address {{._customerEmailAddress}}.',
            type: 'TEXT',
          },
          nextId: 'verify_auth_code',
        },
      },
      {
        type: 'IVR_NODE_MESSAGE',
        ivrNode: {
          id: 'auth_code_success',
          name: 'auth code verified',
          description: 'When the customer provides the correct code',
          message: {
            message: 'Thanks! We have successfully verified your account.',
            type: 'TEXT',
          },
          nextId: 'order_data',
        },
      },
      {
        type: 'IVR_NODE_MESSAGE',
        ivrNode: {
          id: 'auth_code_failure',
          name: 'wrong auth code',
          description: "When the customer doesn't provide the correct code",
          message: {
            message:
              'Oops, looks like the code you provided is incorrect. But don’t worry, one of our support heroes will be with you shortly to help.',
            type: 'TEXT',
          },
          nextId: 'route_to_hero',
        },
      },
      {
        type: 'IVR_NODE_MESSAGE',
        ivrNode: {
          id: 'error',
          name: 'error',
          description: 'general error message',
          message: {
            message:
              'Ooops, we encountered an issue while looking up your order. But don’t worry, one of our Support Heroes will be with you shortly to help.',
            type: 'TEXT',
          },
          nextId: 'route_to_hero',
        },
      },
      {
        type: 'IVR_NODE_EMAIL_AUTH_CODE',
        ivrNode: {
          id: 'email_auth_code',
          name: 'email auth code',
          description: 'email auth code to customer',
          emailFromAddress: '',
          emailToAddress: '{{._customerEmailAddress}}',
          answerId: undefined,
          answerLanguage: 'en-us',
          nextId: 'customer_id',
          errorNodeId: 'error',
        },
      },
      {
        type: 'IVR_NODE_MESSAGE',
        ivrNode: {
          id: 'goodbye',
          name: 'goodbye',
          description: 'goodbye',
          message: {
            message: 'Feel free to chat again if you have more questions. Have a great day!',
            type: 'TEXT',
          },
          nextId: '',
        },
      },
    ],
  };
};

const getNewThread = (): ThreadWithNodeMap => {
  const template = newThreadTemplate() as Thread;

  const transformed = { ...omit(template, ['nodes']), nodesMap: keyBy(template.nodes, 'ivrNode.id') };

  forEach(FIELD_MAPPER, field => {
    if (field.defaultValue) {
      set(transformed, field.path, field.defaultValue);
    }
  });

  return transformed;
};

export default getNewThread();
